import React, { useState, useEffect } from 'react';
import { Button, Select } from 'antd';
import ApplicationService from '../services/axios.service';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Spinner from './spinner';
import ExcelJS from 'exceljs';
import { FaFileExcel } from 'react-icons/fa';

const ForecastingVsBudgetNew = () => {
  const currentYear = new Date().getFullYear();
  
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedQuarter, setSelectedQuarter] = useState(['Q1', 'Q2', 'Q3', 'Q4']);
  const [data, setData] = useState([]);
  const [pivotData, setPivotData] = useState([]);
  const [monthHeaders, setMonthHeaders] = useState([]);
  const [loading, setLoading] = useState(false);
  const service = new ApplicationService();

  // Month mapping for financial year
  const monthMap = {
    '04': 'Apr', '05': 'May', '06': 'Jun',
    '07': 'Jul', '08': 'Aug', '09': 'Sep',
    '10': 'Oct', '11': 'Nov', '12': 'Dec',
    '01': 'Jan', '02': 'Feb', '03': 'Mar'
  };

  const getMonthsForQuarters = (quarters, year) => {
    const quarterMonths = {
      Q1: ['04', '05', '06'],
      Q2: ['07', '08', '09'],
      Q3: ['10', '11', '12'],
      Q4: ['01', '02', '03']
    };

    let months = [];
    quarters.forEach(q => {
      if (quarterMonths[q]) {
        quarterMonths[q].forEach(month => {
          const actualYear = (q === 'Q4') ? parseInt(year) + 1 : year;
          months.push({
            key: `${actualYear}-${month}`,
            label: monthMap[month],
            month: month,
            year: actualYear
          });
        });
      }
    });
    return months;
  };

  const transformToPivot = (rawData, months) => {
    const customerMap = new Map();

    rawData.forEach(row => {
      if (!customerMap.has(row.customer_id)) {
        customerMap.set(row.customer_id, {
          customer_id: row.customer_id,
          customer_name: row.customer_name,
          months: {}
        });
      }

      const customer = customerMap.get(row.customer_id);
      customer.months[row.month] = {
        budget: parseFloat(row.budget || 0),
        forecast: parseFloat(row.forecast || 0),
        actuals: parseFloat(row.actuals || 0)
      };
    });

    return Array.from(customerMap.values());
  };

  const fetchData = async () => {
    if (selectedQuarter.length === 0) {
      toast.warning('Please select at least one quarter');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        year: selectedYear.toString(),
        quarter: selectedQuarter.join(','),
      };
      
      const response = await service.getBudgetForecastActuals(payload);
      const result = response.data;
      
      if (result.success && result.data) {
        const months = getMonthsForQuarters(selectedQuarter, selectedYear);
        setMonthHeaders(months);
        
        const pivot = transformToPivot(result.data, months);
        setPivotData(pivot);
        setData(result.data);
        
        toast.success('Data fetched successfully!');
      } else {
        setData([]);
        setPivotData([]);
        setMonthHeaders([]);
        toast.info('No data found');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
      setData([]);
      setPivotData([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to check if a month is in the past, current, or future
  const getMonthStatus = (monthKey) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = String(now.getMonth() + 1).padStart(2, '0');
    const currentYearMonth = `${currentYear}-${currentMonth}`;
    
    if (monthKey < currentYearMonth) return 'past';
    if (monthKey === currentYearMonth) return 'current';
    return 'future';
  };

  const fetchYears = async () => {
    try {
      const response = await service.getBudgetForecastActualsYears();
      if (response.data.statusCode === 200) {
        const years = response.data.data || [];
        const uniqueYears = [...new Set(years.map(year => Number(year)))].sort((a, b) => b - a);
        setAvailableYears(uniqueYears);
        if (uniqueYears.length > 0) {
          setSelectedYear(uniqueYears[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching years:', error);
      setAvailableYears([currentYear, currentYear - 1, currentYear - 2]);
    }
  };

  useEffect(() => {
    fetchYears();
  }, []);

  useEffect(() => {
    if (selectedQuarter.length > 0) {
      fetchData();
    }
  }, [selectedYear, selectedQuarter]);

  const handleSearch = () => {
    fetchData();
  };

  const handleExport = async () => {
    try {
      if (!pivotData || pivotData.length === 0) {
        toast.error('No data to export');
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Budget Forecast Actuals');

      // Fixed columns
      const fixedColumns = ['Customer Name'];
      
      // Create first header row (Month names merged)
      const headerRow1 = [...fixedColumns];
      monthHeaders.forEach(month => {
        const status = getMonthStatus(month.key);
        let colCount = 1; // Budget always shown
        if (status === 'past' || status === 'current') colCount++; // + Actual
        if (status === 'current' || status === 'future') colCount++; // + FC
        
        headerRow1.push(month.label);
        for (let i = 1; i < colCount; i++) {
          headerRow1.push('');
        }
      });
      worksheet.addRow(headerRow1);

      // Create second header row (Column names)
      const headerRow2 = [...fixedColumns];
      monthHeaders.forEach(month => {
        const status = getMonthStatus(month.key);
        headerRow2.push('Budget');
        if (status === 'past' || status === 'current') headerRow2.push('Actual');
        if (status === 'current' || status === 'future') headerRow2.push('FC');
      });
      worksheet.addRow(headerRow2);

      // Merge cells for month headers
      let currentCol = fixedColumns.length + 1;
      monthHeaders.forEach(month => {
        const status = getMonthStatus(month.key);
        let colCount = 1;
        if (status === 'past' || status === 'current') colCount++;
        if (status === 'current' || status === 'future') colCount++;
        
        const startCol = currentCol;
        const endCol = currentCol + colCount - 1;
        if (startCol !== endCol) {
          worksheet.mergeCells(1, startCol, 1, endCol);
        }
        currentCol = endCol + 1;
      });

      // Style header rows
      worksheet.getRow(1).font = { bold: true, size: 11 };
      worksheet.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFFEB3B' }
      };
      worksheet.getRow(1).alignment = { horizontal: 'center', vertical: 'middle' };
      worksheet.getRow(1).height = 25;

      worksheet.getRow(2).font = { bold: true, size: 10 };
      worksheet.getRow(2).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE0E0E0' }
      };
      worksheet.getRow(2).alignment = { horizontal: 'center', vertical: 'middle' };
      worksheet.getRow(2).height = 20;

      // Add data rows
      pivotData.forEach(customer => {
        const row = [customer.customer_name];

        monthHeaders.forEach(month => {
          const status = getMonthStatus(month.key);
          const monthData = customer.months[month.key] || { budget: 0, forecast: 0, actuals: 0 };
          
          row.push(Number(monthData.budget) || 0);
          if (status === 'past' || status === 'current') row.push(Number(monthData.actuals) || 0);
          if (status === 'current' || status === 'future') row.push(Number(monthData.forecast) || 0);
        });

        worksheet.addRow(row);
      });

      // Auto-fit columns
      worksheet.columns.forEach((column, index) => {
        if (index === 0) {
          column.width = 25;
        } else {
          column.width = 12;
          column.numFmt = '#,##0.00';
        }
      });

      // Add borders to all cells
      worksheet.eachRow((row, rowNumber) => {
        row.eachCell((cell) => {
          cell.border = {
            top: { style: 'thin', color: { argb: 'FF000000' } },
            left: { style: 'thin', color: { argb: 'FF000000' } },
            bottom: { style: 'thin', color: { argb: 'FF000000' } },
            right: { style: 'thin', color: { argb: 'FF000000' } }
          };
          
          // Align data cells
          if (rowNumber > 2) {
            cell.alignment = { horizontal: 'right', vertical: 'middle' };
          }
        });
        
        // Left align customer name
        if (rowNumber > 2) {
          row.getCell(1).alignment = { horizontal: 'left', vertical: 'middle' };
        }
      });

      // Generate file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `budget_forecast_actuals_${selectedYear}_Q${selectedQuarter.join('_')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel file downloaded successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export Excel file');
    }
  };

  return (
    <div className="p-4 bg-[#f9fafb]">
      <h2 className="text-2xl font-bold mb-4">Budget, Forecast & Actuals</h2>

      {/* Filter Section */}
      <div className="bg-white p-4 rounded-lg shadow-md mb-4">
        <div className="flex items-center gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">Year</label>
            <Select
              value={selectedYear}
              onChange={setSelectedYear}
              style={{ width: 120 }}
              options={availableYears.map(year => ({ label: year, value: year }))}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Quarter</label>
            <Select
              mode="multiple"
              value={selectedQuarter}
              onChange={setSelectedQuarter}
              style={{ width: 240 }}
              placeholder="Select quarters"
              options={[
                { label: 'Q1', value: 'Q1' },
                { label: 'Q2', value: 'Q2' },
                { label: 'Q3', value: 'Q3' },
                { label: 'Q4', value: 'Q4' }
              ]}
            />
          </div>

          <div style={{ marginTop: '24px' }}>
            <button
              onClick={handleSearch}
              className="bg-[#3D3D3D] text-white px-6 py-2 rounded-md cursor-pointer hover:bg-[#2D2D2D]"
              style={{ height: '40px' }}
            >
              <span className="text-white">Search</span>
            </button>
          </div>

          {pivotData.length > 0 && (
            <div style={{ marginTop: '24px' }}>
              <button
                onClick={handleExport}
                className="bg-green-600 text-white px-4 py-2 rounded-md flex items-center gap-2 hover:bg-green-700"
                style={{ height: '40px' }}
              >
                <FaFileExcel />
                Export Excel
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Loading Spinner */}
      {loading ? (
        <div className="flex justify-center items-center py-10">
          <Spinner />
        </div>
      ) : (
        <div className="bg-white p-4 rounded-lg shadow-md">
          {pivotData.length > 0 ? (
            <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
              <table className="min-w-full border-collapse border border-gray-300">
                <thead className="sticky top-0 z-20">
                  <tr className="bg-[#EAEEFF]">
                    <th className="border border-gray-300 px-4 py-2 text-left sticky left-0 bg-[#EAEEFF] z-30" style={{ minWidth: '150px' }}>
                      Models
                    </th>
                    {monthHeaders.map((month, idx) => {
                      const status = getMonthStatus(month.key);
                      return (
                        <React.Fragment key={idx}>
                          <th className="border border-gray-300 px-2 py-2 text-center bg-[#E8E8FF]" style={{ minWidth: '90px', fontSize: '11px' }}>
                            {month.label} Budget
                          </th>
                          {(status === 'past' || status === 'current') && (
                            <th className="border border-gray-300 px-2 py-2 text-center bg-[#D4A5D4]" style={{ minWidth: '90px', fontSize: '11px' }}>
                              {month.label} ACTUAL
                            </th>
                          )}
                          {(status === 'current' || status === 'future') && (
                            <th className="border border-gray-300 px-2 py-2 text-center bg-[#FFE8CC]" style={{ minWidth: '90px', fontSize: '11px' }}>
                              {month.label} FC
                            </th>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {pivotData.map((customer, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="border border-gray-300 px-4 py-2 font-medium sticky left-0 bg-white z-10" style={{ minWidth: '150px' }}>
                        {customer.customer_name}
                      </td>
                      {monthHeaders.map((month, mIdx) => {
                        const monthData = customer.months[month.key] || { budget: 0, actuals: 0, forecast: 0 };
                        const status = getMonthStatus(month.key);
                        
                        return (
                          <React.Fragment key={mIdx}>
                            <td className="border border-gray-300 px-2 py-2 text-center" style={{ minWidth: '90px', fontSize: '11px' }}>
                              {monthData.budget.toFixed(2)}
                            </td>
                            {(status === 'past' || status === 'current') && (
                              <td className="border border-gray-300 px-2 py-2 text-center bg-[#F9F0F9]" style={{ minWidth: '90px', fontSize: '11px' }}>
                                {monthData.actuals.toFixed(2)}
                              </td>
                            )}
                            {(status === 'current' || status === 'future') && (
                              <td className="border border-gray-300 px-2 py-2 text-center bg-[#FFF8F0]" style={{ minWidth: '90px', fontSize: '11px' }}>
                                {monthData.forecast.toFixed(2)}
                              </td>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No data available</p>
          )}
        </div>
      )}
    </div>
  );
};

export default ForecastingVsBudgetNew;

